import { FC } from "react"
import { Component } from "../../types/ContentStructure"
import { Box, Typography } from "@mui/material"
import ComponentLink from "./ComponentLink"

interface IComponentBannerProps {
    component: Component
    sx?: any
}

const ComponentBanner: FC<IComponentBannerProps> = ({ component, sx }) => {
    const { theme, title, subTitle, text, image, imageMd, imageXs, components } = component;

    // Patrón de fallback por breakpoint:
    // xs (mobile)  → imageXs || image
    // sm (tablet)  → imageMd || image
    // md (desktop) → image
    const bgXs = imageXs || image;
    const bgMd = imageMd || image;
    const bg = image;

    return (
        <Box sx={[
            theme.themeStyles,
            {
                position: 'relative',
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                backgroundImage: {
                    xs: `url(${bgXs})`,
                    md: `url(${bgMd})`,
                    lg: `url(${bg})`,
                },
                backgroundSize: 'cover',
                backgroundPosition: {
                    xs: 'top center',
                    md: 'top center',
                    lg: 'center right',
                },
                backgroundRepeat: 'no-repeat',
                minHeight: { xs: '100svh', sm: '85vh', md: '100svh' },
                width: '100%',
                overflow: 'hidden',
            },
            sx
        ]}>
            {/* Gradiente overlay izquierdo — texto legible sobre la imagen */}
            <Box sx={{
                position: 'absolute',
                inset: 0,
                background: {
                    xs: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 100%)',
                    md: 'linear-gradient(to right, rgba(0,0,0,0.60) 0%, rgba(0,0,0,0.25) 55%, transparent 80%)',
                },
                zIndex: 1,
            }} />

            {/* Contenido — columna izquierda */}
            <Box sx={{
                width: '100%',
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: { xs: 'flex-end', md: 'flex-end', lg: 'center' },
                alignItems: { xs: 'center', md: 'center', lg: 'flex-start' },
                px: { xs: 3, sm: 6, md: 10 },
                py: { xs: 0, md: 0, lg: 6 },
                pb: { xs: 15, md: 10 },

                color: 'white',
                minHeight: 'inherit',
                textAlign: { xs: 'center', md: 'center', lg: 'justify' },
            }}>
                {/* Título principal */}
                {title && (
                    <Typography variant="h1" sx={{
                        color: 'white',
                        mb: 1.5,
                        textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                        lineHeight: 1.1,
                        textAlign: 'justify',
                    }}>
                        {title}
                    </Typography>
                )}

                {/* Subtítulo */}
                {subTitle && (
                    <Typography variant="h6" sx={{
                        color: 'white',
                        mb: 1,
                        textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        maxWidth: '45ch',
                    }}>
                        {subTitle}
                    </Typography>
                )}

                {/* Cuerpo */}
                {text && (
                    <Typography variant="body1" sx={{
                        color: 'rgba(255,255,255,0.90)',
                        mb: 3,
                        maxWidth: '45ch',
                        textAlign: 'justify',
                        textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }}>
                        {text}
                    </Typography>
                )}

                {/* CTAs */}
                {components.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                        {components.map((item: Component, idx: number) => (
                            <ComponentLink key={idx} component={item} variant='cta' />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default ComponentBanner
